/**
 * Inverter catalog — popular AU-market models with pre-filled spec values.
 *
 * This is a starter catalog (~25 entries) covering the inverters Solar Proof
 * customers most commonly install. Users can still hand-edit any value
 * after picking from the catalog if their unit deviates slightly.
 *
 * Sources: published manufacturer datasheets (capacity, phases, MPPT count,
 * input current, MPPT voltage range, recommended strings per MPPT).
 *
 * To add: append an entry. The shape is intentionally narrow — anything
 * specific to one MPPT (different per-MPPT current limits, etc) gets
 * applied uniformly here, then the user edits the affected MPPT.
 */

export interface InverterCatalogEntry {
  manufacturer: string;
  model: string;
  capacity: number;            // W (AC nominal)
  phases: 1 | 3;
  type: 'string' | 'hybrid' | 'microinverter';
  mpptCount: number;
  maxCurrentPerMppt: number;   // A (DC, per MPPT)
  voltageRange: [number, number];
  /** Typical strings-per-MPPT for residential/commercial use. */
  defaultStringsPerMppt: number;
  /** Optional short note shown in the picker (e.g. "battery-ready"). */
  note?: string;
}

export const INVERTER_CATALOG: InverterCatalogEntry[] = [
  // ----- Sungrow -----
  { manufacturer: 'Sungrow', model: 'SG3.0RS',  capacity: 3000,   phases: 1, type: 'string', mpptCount: 2,  maxCurrentPerMppt: 16, voltageRange: [40,  600], defaultStringsPerMppt: 1 },
  { manufacturer: 'Sungrow', model: 'SG5.0RS',  capacity: 5000,   phases: 1, type: 'string', mpptCount: 2,  maxCurrentPerMppt: 16, voltageRange: [40,  600], defaultStringsPerMppt: 1 },
  { manufacturer: 'Sungrow', model: 'SG10RS',   capacity: 10000,  phases: 1, type: 'string', mpptCount: 2,  maxCurrentPerMppt: 16, voltageRange: [40,  600], defaultStringsPerMppt: 1 },
  { manufacturer: 'Sungrow', model: 'SG10RT',   capacity: 10000,  phases: 3, type: 'string', mpptCount: 2,  maxCurrentPerMppt: 13.5, voltageRange: [160, 1000], defaultStringsPerMppt: 1 },
  { manufacturer: 'Sungrow', model: 'SH10RT',   capacity: 10000,  phases: 3, type: 'hybrid', mpptCount: 2,  maxCurrentPerMppt: 15, voltageRange: [150, 950],  defaultStringsPerMppt: 1, note: 'Battery-ready (DC)' },
  { manufacturer: 'Sungrow', model: 'SH25T',    capacity: 25000,  phases: 3, type: 'hybrid', mpptCount: 3,  maxCurrentPerMppt: 30, voltageRange: [200, 1000], defaultStringsPerMppt: 2, note: 'Battery-ready (DC)' },
  { manufacturer: 'Sungrow', model: 'SG33CX',   capacity: 33000,  phases: 3, type: 'string', mpptCount: 3,  maxCurrentPerMppt: 26, voltageRange: [200, 1000], defaultStringsPerMppt: 2 },
  { manufacturer: 'Sungrow', model: 'SG50CX',   capacity: 50000,  phases: 3, type: 'string', mpptCount: 5,  maxCurrentPerMppt: 30, voltageRange: [200, 1000], defaultStringsPerMppt: 2 },
  { manufacturer: 'Sungrow', model: 'SG110CX',  capacity: 110000, phases: 3, type: 'string', mpptCount: 10, maxCurrentPerMppt: 26, voltageRange: [200, 1000], defaultStringsPerMppt: 2 },

  // ----- SMA -----
  { manufacturer: 'SMA',     model: 'SBS5.0-10',         capacity: 5000,  phases: 1, type: 'string', mpptCount: 4, maxCurrentPerMppt: 15, voltageRange: [100, 550], defaultStringsPerMppt: 1 },
  { manufacturer: 'SMA',     model: 'Sunny Boy 5.0',     capacity: 5000,  phases: 1, type: 'string', mpptCount: 2, maxCurrentPerMppt: 15, voltageRange: [100, 500], defaultStringsPerMppt: 1 },
  { manufacturer: 'SMA',     model: 'Sunny Tripower 8.0', capacity: 8000, phases: 3, type: 'string', mpptCount: 2, maxCurrentPerMppt: 20, voltageRange: [140, 800], defaultStringsPerMppt: 1 },

  // ----- Fronius -----
  { manufacturer: 'Fronius', model: 'Primo 5.0-1',  capacity: 5000,  phases: 1, type: 'string', mpptCount: 2, maxCurrentPerMppt: 18, voltageRange: [80,  1000], defaultStringsPerMppt: 1 },
  { manufacturer: 'Fronius', model: 'Primo 8.2-1',  capacity: 8200,  phases: 1, type: 'string', mpptCount: 2, maxCurrentPerMppt: 18, voltageRange: [80,  1000], defaultStringsPerMppt: 1 },
  { manufacturer: 'Fronius', model: 'Symo 10.0-3',  capacity: 10000, phases: 3, type: 'string', mpptCount: 2, maxCurrentPerMppt: 27, voltageRange: [200, 1000], defaultStringsPerMppt: 1 },
  { manufacturer: 'Fronius', model: 'Symo 15.0-3',  capacity: 15000, phases: 3, type: 'string', mpptCount: 2, maxCurrentPerMppt: 27, voltageRange: [200, 1000], defaultStringsPerMppt: 1 },

  // ----- GoodWe -----
  { manufacturer: 'GoodWe',  model: 'GW6000-DT',     capacity: 6000,  phases: 3, type: 'string', mpptCount: 2, maxCurrentPerMppt: 11, voltageRange: [180, 1000], defaultStringsPerMppt: 1 },
  { manufacturer: 'GoodWe',  model: 'GW10K-ET',      capacity: 10000, phases: 3, type: 'hybrid', mpptCount: 2, maxCurrentPerMppt: 16, voltageRange: [200, 1000], defaultStringsPerMppt: 1, note: 'Battery-ready (DC)' },

  // ----- SolarEdge -----
  { manufacturer: 'SolarEdge', model: 'SE5000H', capacity: 5000,  phases: 1, type: 'string', mpptCount: 1, maxCurrentPerMppt: 13.5, voltageRange: [320, 480], defaultStringsPerMppt: 1, note: 'Requires DC optimisers' },
  { manufacturer: 'SolarEdge', model: 'SE10000H', capacity: 10000, phases: 1, type: 'string', mpptCount: 1, maxCurrentPerMppt: 27,   voltageRange: [320, 480], defaultStringsPerMppt: 2, note: 'Requires DC optimisers' },

  // ----- Huawei -----
  { manufacturer: 'Huawei',  model: 'SUN2000-3KTL-L1',  capacity: 3000,  phases: 1, type: 'string', mpptCount: 2, maxCurrentPerMppt: 11, voltageRange: [90, 560],  defaultStringsPerMppt: 1 },
  { manufacturer: 'Huawei',  model: 'SUN2000-10KTL-M1', capacity: 10000, phases: 3, type: 'string', mpptCount: 2, maxCurrentPerMppt: 26, voltageRange: [140, 980], defaultStringsPerMppt: 1 },
  { manufacturer: 'Huawei',  model: 'SUN2000-30KTL',    capacity: 30000, phases: 3, type: 'string', mpptCount: 4, maxCurrentPerMppt: 26, voltageRange: [200, 1000], defaultStringsPerMppt: 1 },

  // ----- Tesla / Enphase (microinverter) — special cases -----
  { manufacturer: 'Tesla',   model: 'Powerwall 3 Inverter', capacity: 11500, phases: 1, type: 'hybrid', mpptCount: 6, maxCurrentPerMppt: 13, voltageRange: [60, 550], defaultStringsPerMppt: 1, note: 'Integrated battery inverter' },
  { manufacturer: 'Enphase', model: 'IQ8M+',               capacity: 330,   phases: 1, type: 'microinverter', mpptCount: 1, maxCurrentPerMppt: 14, voltageRange: [27, 60], defaultStringsPerMppt: 1, note: 'One per panel' },
];

/**
 * Convert a catalog entry into a fresh Inverter topology object with the
 * user-specified strings per MPPT (defaults to entry's typical value).
 */
import type { Inverter } from '../types/topology';

export function inverterFromCatalog(
  entry: InverterCatalogEntry,
  opts: { id: string; panelsPerString?: number; stringsPerMppt?: number } = { id: 'inv1' }
): Inverter {
  const stringsPerMppt = opts.stringsPerMppt ?? entry.defaultStringsPerMppt;
  const panels = opts.panelsPerString ?? 10;
  return {
    id: opts.id,
    manufacturer: entry.manufacturer,
    model: entry.model,
    capacity: entry.capacity,
    phases: entry.phases,
    type: entry.type,
    mppts: Array.from({ length: entry.mpptCount }, (_, i) => ({
      mpptNumber: i + 1,
      maxCurrent: entry.maxCurrentPerMppt,
      voltageRange: entry.voltageRange,
      strings: Array.from({ length: stringsPerMppt }, () => ({ panels })),
    })),
  };
}
